import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import type { AutoTransferConfig, AutoTransferRule } from '../types/auto-transfer.types';

export type ReturnWindowUnit = 'HOURS' | 'DAYS' | 'MONTHS';

export interface SetReturnWindowParams {
  alwaysReturn?: boolean;
  returnWindowValue?: number;
  returnWindowUnit?: ReturnWindowUnit;
}

interface UseAutoTransferRulesReturn {
  config: AutoTransferConfig | null;
  loading: boolean;
  error: string | null;
  toggleActive: (isActive: boolean) => Promise<void>;
  createRule: (data: CreateRuleInput) => Promise<AutoTransferRule>;
  updateRule: (ruleId: string, data: Partial<CreateRuleInput>) => Promise<void>;
  deleteRule: (ruleId: string) => Promise<void>;
  reorderRules: (ruleIds: string[]) => Promise<void>;
  setDefaultAssignedUser: (userId: string | null) => Promise<void>;
  setReturnWindow: (params: SetReturnWindowParams) => Promise<void>;
  refetch: () => Promise<void>;
}

interface CreateRuleInput {
  name: string;
  conditions: Record<string, unknown>;
  assignedUserIds: string[];
}

export function useAutoTransferRules(): UseAutoTransferRulesReturn {
  const [config, setConfig] = useState<AutoTransferConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.get<AutoTransferConfig>('/leads/auto-transfer');
      setConfig(data);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load auto-transfer config';
      setError(errorMessage);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const toggleActive = async (isActive: boolean) => {
    await apiService.patch('/leads/auto-transfer/toggle', { isActive });
    await fetchConfig();
  };

  const createRule = async (data: CreateRuleInput): Promise<AutoTransferRule> => {
    const rule = await apiService.post<AutoTransferRule>('/leads/auto-transfer/rules', data);
    await fetchConfig();
    return rule;
  };

  const updateRule = async (ruleId: string, data: Partial<CreateRuleInput>) => {
    await apiService.patch(`/leads/auto-transfer/rules/${ruleId}`, data);
    await fetchConfig();
  };

  const deleteRule = async (ruleId: string) => {
    await apiService.delete(`/leads/auto-transfer/rules/${ruleId}`);
    await fetchConfig();
  };

  const setDefaultAssignedUser = async (userId: string | null): Promise<void> => {
    await apiService.patch('/leads/auto-transfer/default-assigned-user', { userId });
    await fetchConfig();
  };

  const setReturnWindow = async (params: SetReturnWindowParams): Promise<void> => {
    await apiService.patch('/leads/auto-transfer/return-rule', params);
    await fetchConfig();
  };

  const reorderRules = async (ruleIds: string[]) => {
    // Optimistic update for smooth UX
    setConfig(prev => {
      if (!prev) return null;
      const reordered = ruleIds.map((id, index) => {
        const rule = prev.rules.find(r => r.id === id);
        return rule ? { ...rule, priority: index } : null;
      }).filter(Boolean) as AutoTransferRule[];
      return { ...prev, rules: reordered };
    });

    try {
      await apiService.put('/leads/auto-transfer/rules/reorder', { ruleIds });
    } catch (error) {
      // Revert on error
      await fetchConfig();
      throw error;
    }
  };

  return {
    config,
    loading,
    error,
    toggleActive,
    createRule,
    updateRule,
    deleteRule,
    reorderRules,
    setDefaultAssignedUser,
    setReturnWindow,
    refetch: fetchConfig,
  };
}